import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CardSetTransformationService, CoreService, DataService, DialogService, SaveDataRequest, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { APP_DATA_KEYS, POP_UP_CODES, SAVE_RESULT_STATUS, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { FormDataService } from '../../services/form-data.service';
import { BaseCardSetDetailComponent } from "../base-card-set-detail-component/base-card-set-detail.component";

@Component({
  selector: 'app-reference-data',
  templateUrl: './reference-data.component.html',
  styleUrls: ['./reference-data.component.scss']
})
export class ReferenceDataComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;

  id = '';
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;
  templateId: number = TEMPLATES.referenceData;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;
  isVPTASPreApplicationQuestionnaire = false;
  private isViewModeForCategory = true;
  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected errorService: ErrorService,
    protected coreService: CoreService,
    protected dataService: DataService,
    protected dialogService: DialogService,
    protected spinnerService: SpinnerService,
    protected appDataService: AppDataService,
    protected formDataService: FormDataService,
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = id;
          const segments = this.route.snapshot.url.map(s => s.path);
          if(segments[0] == ROUTE_CONSTANTS.victorianPatientTransportAssistanceSchemePreApplicationQuestionnaire.prefix){
            this.isVPTASPreApplicationQuestionnaire = true;
            this.formServiceEndpoint = API_ENDPOINT_KEYS.getVictorianPatientTransportAssistanceSchemeReferenceDataConfigAndResponse;
          }
          else{
            this.formServiceEndpoint = API_ENDPOINT_KEYS.getReferenceDataConfigAndResponse;
          }
          this.editing = true;
        }
        else {
          this.resetDirtyFlagAfterLoading();
        }
      })
    );

    this.coreService.conditionFunctions['isEditing'] = () => this.editing;
    this.coreService.conditionFunctions['isEditingRefValue'] = (pageIndex: number, sectionIndex: number) => {
      return this.formDataService.referenceDataFormIsEditingRefValue(this.baseForm.model, sectionIndex);
    };

    this.formDataService
      .configureReferenceDataFormViewMode(
        () => this.baseForm.fields,
        () => this.editing,
        (enabled: boolean) => this.isViewModeForCategory = enabled,
        () => this.isViewModeForCategory);

    this.coreService.repeatingSectionFunctions = {
      isReferenceDataRemoveVisible: (itemIndex?: number): boolean => !this.editing
    };
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }
  formLoaded($event) {
    if (this.baseForm && this.baseForm.model) {
      this.appDataService.setAppData(APP_DATA_KEYS.previousModel, JSON.parse(JSON.stringify(this.baseForm.model)));
    }
  }

  save(event: any) {
    // Prevent redundant save when nothing has changed
    const prev = this.appDataService.getAppData(APP_DATA_KEYS.previousModel);
    const noChanges = !this.baseForm?.form?.dirty || JSON.stringify(prev) === JSON.stringify(this.baseForm?.model);
    if (noChanges) { return; }

    const saveDataRequest: SaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: false
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  cancel(event: any) {
    this.navigateBackToDashboard();
  }

  dialogSubmitted(event: any) {
    if (this.activeDialog === POP_UP_CODES.common.saveSuccessful && this.isVPTASPreApplicationQuestionnaire != true) {
      this.navigateBackToDashboard(true);
    }
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.referenceData.list], ignoreUnsavedChanges);
  }

  private saveData(request: SaveDataRequest) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.dataService.saveData(API_ENDPOINTS.referenceData.save, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.saveReferenceData.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              // To avoid false unsaved prompts
              this.baseForm.lastUpdatedTime = data.allocatedLastUpdatedTime;
              this.setPreviousModel({ isAsync: true, maxAttempts: 5, delayBetweenAttemptsMs: 150 });
              this.activeDialog = POP_UP_CODES.common.saveSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          }
        })
    );

  }

}
